import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilmStripIcon } from '@phosphor-icons/react';
import { Chip } from '@/components';
import { Popover } from '@/components/Popover';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useSourcesStore } from '@/stores';
import { SourceType } from '@/types/source';
import { SecurityCameraIconMirrored } from '@/utils/icons';

interface SourcesPopoverProps {
    sourceIds: string[];
    isExclusionMode?: boolean;
    onRemoveSource?: (id: string) => void;
    onSelectSource?: (id: string) => void;
    onDeselectSource?: (id: string) => void;
}

export function SourcesPopover({
    sourceIds,
    isExclusionMode = false,
    onRemoveSource,
    onSelectSource,
    onDeselectSource,
}: SourcesPopoverProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const store = useSourcesStore();
    const selectSource = onSelectSource ?? store.selectSource;
    const deselectSource = onDeselectSource ?? store.deselectSource;
    const getSourceById = store.getSourceById;

    const handleRemoveSource = (sourceId: string) => {
        if (onRemoveSource) {
            onRemoveSource(sourceId);
        } else if (isExclusionMode) {
            selectSource(sourceId);
        } else {
            deselectSource(sourceId);
        }
    };

    const getSourceIcon = (type: SourceType) => {
        switch (type) {
            case SourceType.CAMERA:
            case SourceType.FLIR:
                return <SecurityCameraIconMirrored className="size-4" />;
            case SourceType.FILE:
                return <FilmStripIcon className="size-4" />;
            default:
                return <SecurityCameraIconMirrored className="size-4" />;
        }
    };

    return (
        <Popover
            title={t('filters.selectedSourcesTitle')}
            className="max-h-[300px] w-[240px] overflow-y-auto scrollbar-grey-600"
        >
            <div className="flex flex-col gap-2">
                {sourceIds.map((sourceId) => {
                    const source = getSourceById(sourceId);
                    if (!source) return null;

                    return (
                        <Chip
                            key={sourceId}
                            icon={getSourceIcon(source.type)}
                            value={source.displayName ?? source.name}
                            onRemove={() => handleRemoveSource(sourceId)}
                        />
                    );
                })}
            </div>
        </Popover>
    );
}
